import React, { useEffect, useState, useCallback } from 'react'
import { useFormContext } from 'react-hook-form'
import { v4 as uuidv4 } from 'uuid'
import { LinearProgress, Grid, TextField, Button, Stack, Typography } from '@mui/material'
import { RegisteredCheckBox } from '../../../../components/fields/CheckBox'
import { InvestorsFieldsSanityCheckProps } from './AdminInvestorFieldsSanityCheck'
import { InvestorsTable } from './AdminInvestorsTable'
import { ShouldShow } from '../../../../components/entities/IssueType'
import { FormIssueEntity } from '../../types/FormIssueEntity'

export type validateFn = (value: string, formValues: InvestorForm) => boolean

export type InvestorForm = {
    name: string
    amountInPercentage: string | null
    amountInCash: string | null
    feeLevel?: string | null
    leo?: boolean | null
    isSeller: boolean | null
    /** Företrädesemission / IPO: teckningsåtagande utan teckningsrätter (endast nivå 0) */
    withoutRights?: boolean | null
}

export type InvestorFormWithId = InvestorForm & { tempId?: string; id?: string }

/**
 * Cleans the value string. Removes leading and trailing whitespace. If the value is numerical, it also
 * replaces commas with dots and removes any non-numeric characters (except dots).
 *
 * @param {string} value the value to clean
 * @param {boolean} isNumerical flag to indicate if the value is numerical
 */
const cleanValue = (value: string, isNumerical?: boolean) => {
    if (!value) return value
    value = value.trim()
    // don't allow more than one space character in a row
    value = value.replace(/\s{2,}/g, ' ')
    if (isNumerical) {
        value = value.replace(',', '.').replace(/[^0-9.]/g, '')
    }
    return value
}

function isCommaString(textStr: string) {
    const rows = textStr.trim().split('\n')

    if (rows.length > 1) return false

    return rows[0]?.includes(',')
}

function parseCommaString(commaString: string) {
    const investorNames = commaString.trim().split(', ')

    const lastInvestorName = investorNames.pop() as string
    const correctionChecks = [' och ', ' samt ', ' and ']

    let matchExists = null

    for (const correctionCheck of correctionChecks) {
        if (lastInvestorName?.includes(correctionCheck)) {
            matchExists = correctionCheck
            break
        }
    }

    if (matchExists) {
        const correctedNames = [
            lastInvestorName.slice(0, lastInvestorName.lastIndexOf(matchExists)),
            lastInvestorName.slice(lastInvestorName.lastIndexOf(matchExists) + matchExists.length),
        ]

        investorNames.push(...correctedNames)
    } else {
        investorNames.push(lastInvestorName)
    }

    return investorNames
}

/**
 * Creates a list of objects from a tab separated string, each line
 * representing an object, and the first line has the keys.
 *
 * @param {string} tabString the tab separated string
 */
function parseTabString(tabString: string) {
    if (!tabString) return []
    const numericalFields = ['amount_in_percentage', 'amount_in_cash', 'fee_level']
    const lines = tabString.split('\n')
    const keys = lines[0].split('\t')
    const objects = lines.slice(1).map((line) => {
        const values = line.split('\t')
        const obj: Record<string, unknown> = {}
        keys.forEach((key, i) => {
            const value = cleanValue(values[i], numericalFields.includes(key))
            obj[key] = value
        })
        return obj
    })
    // remove all empty rows
    return objects.filter((obj) => Object.values(obj).some((v) => v)) as unknown as ParsedTabString[]
}

interface ParsedTabString {
    id: string
    name: string
    amount_in_percentage: number
    amount_in_cash: number
    fee_level: number
    leo: string
}

interface InvestorsFieldsProps {
    issue?: FormIssueEntity
    entityType: 'issue' | 'warrant' | 'convertible'
    acceptedFeeLevels: number[]
    currency: string | null
    isFeeLevelEnabled: boolean
    isLeoEnabled: boolean
    /** Företrädesemission / IPO: redigera TÅ utan rätter */
    isWithoutRightsEnabled?: boolean
    isPercentageEnabled?: boolean
    isSellerEnabled?: boolean
    isPutOptionEnabled?: boolean
    noInvestorsNamedChecked: boolean
    sanityChecker: (investors: InvestorFormWithId[]) => InvestorsFieldsSanityCheckProps
    setNoInvestorsNamedChecked?: (val: boolean) => void
    setIsCustomFormDirty: (val: boolean) => void
    validateAmount: validateFn
    validateLeo: validateFn
}

const setTempId = (investor: InvestorFormWithId) => {
    return {
        ...investor,
        tempId: investor.tempId || investor.id || uuidv4(),
    }
}

function InvestorsFields({
    issue,
    entityType,
    acceptedFeeLevels,
    currency,
    isFeeLevelEnabled,
    isLeoEnabled,
    isWithoutRightsEnabled = false,
    isPercentageEnabled = true,
    isSellerEnabled = true,
    isPutOptionEnabled = false,
    noInvestorsNamedChecked,
    sanityChecker,
    setNoInvestorsNamedChecked,
    setIsCustomFormDirty,
    validateAmount,
    validateLeo,
}: InvestorsFieldsProps) {
    const formMethods = useFormContext()
    const formValues = formMethods.getValues()
    const [investors, setInvestors] = useState<InvestorFormWithId[]>(
        ((formValues.investors as InvestorFormWithId[]) || []).map(setTempId),
    )
    const [inputInvestors, setInputInvestors] = useState(
        entityType === 'convertible'
            ? ['id', 'name', 'amount_in_percentage', 'amount_in_cash'].join('\t')
            : entityType === 'issue'
              ? [
                    'id',
                    'name',
                    'amount_in_percentage',
                    'amount_in_cash',
                    'fee_level',
                    'leo',
                    'is_seller',
                    'put_option',
                    ...(isWithoutRightsEnabled ? ['without_rights'] : []),
                ].join('\t')
              : ['id', 'name', 'amount_in_cash', 'fee_level', 'put_option'].join('\t'),
    )
    const [showInputArea, setShowInputArea] = useState(false)

    const allowCommaSeparatedNames =
        issue && ['Directed issue', 'ABB Directed issue', 'Non-cash issue'].includes(issue.type)

    const sortInvestors = (investorList: InvestorForm[]) => {
        // sort by feeLevel ASC, amountInCash DESC, amountInPercentage DESC
        investorList.sort((a, b) => {
            if (a.feeLevel !== b.feeLevel) return a.feeLevel < b.feeLevel ? -1 : 1
            if (a.amountInCash !== b.amountInCash) return b.amountInCash - a.amountInCash
            return b.amountInPercentage - a.amountInPercentage
        })
    }
    const setFormInvestors = useCallback(
        (i: InvestorFormWithId[]) => {
            if (JSON.stringify(i) === JSON.stringify(investors)) return

            const newInvestors = i.map(setTempId) // make sure all investors have a tempId
            sortInvestors(newInvestors)
            setInvestors(newInvestors)
            // issue.investors = investors
            const formInvestors = newInvestors.map((p) => {
                const { tempId, ...rest } = p
                return rest
            })
            formMethods.setValue('investors', formInvestors)
        },
        [formMethods, investors],
    )
    useEffect(() => {
        setFormInvestors(investors)
    }, [investors, setFormInvestors])
    if (!investors) return <LinearProgress />

    const validateFeeLevel = (value: string | null | undefined, values: InvestorForm) => {
        if (values.isSeller) return true
        if (!isFeeLevelEnabled) return true

        if (value === null) return false
        if (value === undefined) return false
        if (+value === 0) return true

        return acceptedFeeLevels.includes(+value)
    }
    const validateName = (value: string) => {
        if (
            entityType === 'warrant' ||
            (issue && !['ABB Directed issue', 'Directed issue', 'Non-cash issue'].includes(issue.type))
        )
            return !!value

        if (value?.match(/^\x3F$/)) return false

        return !!value
    }

    const handleTransfer = () => {
        if (isCommaString(inputInvestors)) {
            if (!allowCommaSeparatedNames) {
                return
            }
            const inv = [
                ...investors,
                ...parseCommaString(inputInvestors).map((name) => ({
                    name,
                    isSeller: false,
                    amountInCash: null,
                    amountInPercentage: null,
                })),
            ]
            console.log({ inv })
            setFormInvestors(inv)
            setInputInvestors('')

            return
        }
        const newInvestors: InvestorForm[] = parseTabString(inputInvestors).map(
            ({ amount_in_cash, amount_in_percentage, fee_level, leo, without_rights, ...i }) => {
                const result: Partial<InvestorForm> = { ...i }
                result.feeLevel = fee_level ? parseInt(fee_level) : null

                if (amount_in_cash) result.amountInCash = parseInt(amount_in_cash)
                if (amount_in_percentage) result.amountInPercentage = parseFloat(amount_in_percentage)
                if (leo) result.leo = leo === '1' || leo === 'true' || (leo !== '0' && !!leo)
                if (without_rights != null && without_rights !== '')
                    result.withoutRights =
                        without_rights === '1' ||
                        without_rights === 'true' ||
                        (without_rights !== '0' && !!without_rights)

                return result as InvestorForm
            },
        )

        const isValid = newInvestors.every(
            (investor) =>
                validateName(investor.name, investor) &&
                validateAmount('', investor) &&
                (!isFeeLevelEnabled || validateFeeLevel(investor.feeLevel, investor)) &&
                (!isLeoEnabled || validateLeo('', investor)),
        )

        if (!isValid) return

        const aggregatedInvestors = investors.concat(newInvestors)
        setFormInvestors(aggregatedInvestors)
        setInputInvestors('')
    }
    const nullEmptyStrings = (obj: Record<string, unknown>) => {
        Object.keys(obj).forEach((key) => {
            if (obj[key] === '') obj[key] = null
        })
    }
    const handleSubmit = (investor: InvestorForm & InvestorFormWithId) => {
        nullEmptyStrings(investor)
        investor.name = cleanValue(investor.name)
        setIsCustomFormDirty(true)
        if (investor.tempId) {
            // update
            const newInvestors = investors.map((p) => (p.tempId === investor.tempId ? investor : p))
            setFormInvestors(newInvestors)
        } else {
            // create
            investor.tempId = uuidv4()
            const newInvestors = investors.concat(investor)
            setFormInvestors(newInvestors)
        }
    }

    const handleDelete = (investor: InvestorFormWithId) => {
        const newInvestors = investors.filter((p) => p.tempId !== investor.tempId)
        setFormInvestors(newInvestors)
    }
    return (
        <Stack>
            <Stack direction='row' spacing={2}>
                {!noInvestorsNamedChecked && (
                    <Button variant='outlined' onClick={() => setShowInputArea(!showInputArea)}>
                        {showInputArea ? 'Dölj' : 'Visa'} massinput-fält
                    </Button>
                )}
                {(!investors || investors.length === 0) && (
                    <RegisteredCheckBox
                        name='noInvestorsNamed'
                        label='Inga kända investerare'
                        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                            setNoInvestorsNamedChecked && setNoInvestorsNamedChecked(event.target.checked)
                        }
                    />
                )}
            </Stack>
            <Grid container spacing={1}>
                {showInputArea && (
                    <>
                        <Grid size={3}>
                            <Stack spacing={2} sx={{ width: '100%', mt: 4 }}>
                                <TextField
                                    sx={{ width: '100%' }}
                                    label='Investare, input'
                                    multiline
                                    maxRows={3}
                                    value={inputInvestors}
                                    onChange={(event) => setInputInvestors(event.target.value)}
                                />
                                <Typography variant='body2'>
                                    Kopiera rubrikerna ovan till ett kalkylblad för rätt struktur, fyll i och kopiera in
                                    här.
                                </Typography>
                                {allowCommaSeparatedNames ? (
                                    <Typography variant='body2'>
                                        Alternativt kopiera in en lista av kommaseparerade namn (RE/ABB/AE).
                                    </Typography>
                                ) : null}
                            </Stack>
                        </Grid>
                        <Grid size={1}>
                            <Button
                                variant='contained'
                                color='primary'
                                fullWidth
                                onClick={handleTransfer}
                                sx={{ mt: 4 }}
                            >
                                &gt;&gt;
                            </Button>
                        </Grid>
                    </>
                )}
                <Grid size={showInputArea ? 8 : 12}>
                    {!noInvestorsNamedChecked && (
                        <InvestorsTable
                            issue={issue}
                            currency={currency}
                            investors={investors}
                            isFeeLevelEnabled={isFeeLevelEnabled}
                            isLeoEnabled={isLeoEnabled}
                            isWithoutRightsEnabled={isWithoutRightsEnabled}
                            isPercentageEnabled={isPercentageEnabled}
                            isSellerEnabled={isSellerEnabled}
                            isPutOptionEnabled={isPutOptionEnabled}
                            sanityChecker={sanityChecker}
                            sx={{ width: '100%' }}
                            handleSubmit={handleSubmit}
                            handleDelete={handleDelete}
                            validateName={validateName}
                            validateAmount={validateAmount}
                            validateFeeLevel={validateFeeLevel}
                            validateLeo={validateLeo}
                        />
                    )}
                </Grid>
                {entityType === 'issue' && issue ? (
                    <>
                        <ShouldShow issueType={issue.type} issueField='investorsAfterTerms'>
                            <Grid size={12}>
                                <RegisteredCheckBox
                                    name='investorsAfterTerms'
                                    label='Uppdaterat i samband med villkor'
                                    onChange={(event) => {
                                        setIsCustomFormDirty(true)
                                        if (!event.target.checked) {
                                            formMethods.setValue('investorsAfterProspectus', false)
                                        }
                                    }}
                                />
                            </Grid>
                        </ShouldShow>
                        <ShouldShow issueType={issue.type} issueField='investorsAfterProspectus'>
                            <Grid size={12}>
                                <RegisteredCheckBox
                                    name='investorsAfterProspectus'
                                    label='Uppdaterat i samband med prospekt'
                                    onChange={(event) => {
                                        setIsCustomFormDirty(true)
                                        if (event.target.checked) {
                                            formMethods.setValue('investorsAfterTerms', true)
                                        }
                                    }}
                                />
                            </Grid>
                        </ShouldShow>
                    </>
                ) : null}
            </Grid>
        </Stack>
    )
}

export default InvestorsFields
